import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {motion, AnimatePresence} from 'framer-motion';
import {useLanguage} from '../contexts/LanguageContext';
import {useTheme} from '../contexts/ThemeContext';
import SafeIcon from '../common/SafeIcon';
import SupportModal from './SupportModal';
import * as FiIcons from 'react-icons/fi';

const {FiMoon, FiSun, FiGlobe, FiHeart} = FiIcons;

const Header = () => {
  const {toggleTheme, isDark: isDarkMode} = useTheme();
  const {t, toggleLanguage, language} = useLanguage();
  const [showSupportModal, setShowSupportModal] = useState(false);
  const displayTitle = language === 'zh-TW'
    ? '線上拍子機｜Beat Master'
    : 'Online Metronome | Beat Master';

  return (
    <header className="w-full py-6">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center justify-center">
          <Link to="/" className="no-underline">
            <motion.h1
              className="text-3xl md:text-4xl font-bold rainbow-text text-center cursor-pointer hover:opacity-90 transition-opacity"
              initial={{opacity: 0, y: -20}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.5}}
            >
              {displayTitle}
            </motion.h1>
          </Link>

          <motion.p
            className="text-gray-600 dark:text-gray-400 mt-2 text-sm md:text-base text-center w-full"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.5, delay: 0.2}}
          >
            {t('appDescription')}
          </motion.p>

          <motion.div
            className="flex items-center mt-5 space-x-3"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.5, delay: 0.4}}
          >
            <motion.button
              onClick={toggleTheme}
              className="p-2 sm:p-3 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              whileHover={{scale: 1.1}}
              whileTap={{scale: 0.95}}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <SafeIcon
                icon={isDarkMode ? FiSun : FiMoon}
                className="w-5 h-5 sm:w-6 sm:h-6"
              />
            </motion.button>

            <motion.button
              onClick={toggleLanguage}
              className="p-2 sm:p-3 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center"
              whileHover={{scale: 1.1}}
              whileTap={{scale: 0.95}}
              aria-label="Change language"
            >
              <SafeIcon icon={FiGlobe} className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="ml-1 text-sm font-medium">
                {language === 'en' ? '中文' : 'EN'}
              </span>
            </motion.button>

            <motion.button
              onClick={() => setShowSupportModal(true)}
              className="p-2 sm:p-3 rounded-full bg-primary-100 dark:bg-primary-900/30 hover:bg-primary-200 dark:hover:bg-primary-800/40 transition-colors flex items-center"
              whileHover={{scale: 1.1}}
              whileTap={{scale: 0.95}}
              aria-label={language === 'en' ? 'Support' : '支持'}
            >
              <span className="heart-icon-container">
                <SafeIcon
                  icon={FiHeart}
                  className="w-5 h-5 sm:w-6 sm:h-6 text-pink-500 dark:text-pink-400"
                />
              </span>
              <span className="ml-1 text-sm font-medium bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
                {language === 'en' ? 'Support' : '支持'}
              </span>
            </motion.button>
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {showSupportModal && (
          <SupportModal onClose={() => setShowSupportModal(false)} />
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;